import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SensorData, SensorItem, MaintenanceStatus } from '../types';
import { formatSensorValue, getEngineHoursStatus, formatTimestamp } from '../utils/formatters';
import { maintenanceAPI } from '../utils/api';
import { Zap, Clock, AlertCircle, Wrench, MapPin, BarChart3, AlertTriangle, Settings } from 'lucide-react';
import { useNotifications } from '../utils/notifications';
import GeneratorCombinedChart from './GeneratorCombinedChart';

interface GeneratorDisplayProps {
    sensor: SensorData;
    allSensors?: SensorData[]; // Optional: all sensors to find fuel sensor for same device
}

const GeneratorDisplay: React.FC<GeneratorDisplayProps> = ({ sensor, allSensors = [] }) => {
    const navigate = useNavigate();
    const { addNotification } = useNotifications();
    const [showChart, setShowChart] = useState(false);
    const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus | null>(null);
    const [loadingMaintenance, setLoadingMaintenance] = useState(false);
    const [notifiedOverdue, setNotifiedOverdue] = useState<Set<number>>(new Set());
    
    // Find fuel sensor for the same device
    const fuelSensor = allSensors.find(
        s => s.type === 'fuel' && s.gpswox_device_id === sensor.gpswox_device_id
    );
    const fuelSensorId = fuelSensor?.id || null;

    // Load maintenance status
    useEffect(() => {
        if (sensor.id) {
            loadMaintenanceStatus();
        }
    }, [sensor.id]);

    const loadMaintenanceStatus = async () => {
        try {
            setLoadingMaintenance(true);
            const response = await maintenanceAPI.getGeneratorStatus(sensor.id);
            setMaintenanceStatus(response.status);
            
            // Show notifications for overdue/due soon maintenance
            if (response.status.overdue > 0) {
                response.status.schedules
                    .filter(s => s.is_overdue && !notifiedOverdue.has(s.id))
                    .forEach(schedule => {
                        addNotification({
                            type: 'error',
                            message: `Maintenance overdue: ${schedule.template_name} for ${sensor.name}`,
                            duration: 10000,
                        });
                        setNotifiedOverdue(prev => new Set(prev).add(schedule.id));
                    });
            } else if (response.status.due_soon > 0) {
                response.status.schedules
                    .filter(s => s.is_due_soon && !s.is_overdue && !notifiedOverdue.has(s.id))
                    .forEach(schedule => {
                        addNotification({
                            type: 'warning',
                            message: `Maintenance due soon: ${schedule.template_name} for ${sensor.name}`,
                            duration: 8000,
                        });
                        setNotifiedOverdue(prev => new Set(prev).add(schedule.id));
                    });
            }
        } catch (error) {
            // Silently fail - maintenance might not be set up yet
            console.log('Maintenance status not available');
        } finally {
            setLoadingMaintenance(false);
        }
    };
    
    // ================================
    // 🔧 Extract Engine Hours Sensor
    // ================================
    const engineHoursSensor =
        sensor.current_data?.sensors?.find((s: SensorItem) => s.type === 'engine_hours') ||
        sensor.device_info?.device_data?.sensors?.find((s: SensorItem) => s.type === 'engine_hours') ||
        null;
    const engineHoursValue = engineHoursSensor?.val ?? null;
    const hasEngineHoursData = engineHoursValue !== null && engineHoursValue !== undefined;
    const displayHours = hasEngineHoursData ? Math.round(Number(engineHoursValue)) : 0;

    // ================================
    // 🔥 Extract Running Status from current_value
    // ================================
    // The running status comes from current_value.value (ignition status)
    const currentValue = sensor.current_value?.value;
    const isRunning = Boolean(
        currentValue === 1 ||
        (typeof currentValue === 'number' && currentValue > 0) ||
        (currentValue as any) === true ||
        (currentValue as any) === "On" ||
        (currentValue as any) === "true"
    );

    // ================================
    // 🌐 Online Status from device_info.status
    // ================================
    const isOnline = sensor.device_info?.status === true;
    const rawOnlineStatus = sensor.current_data?.online; // for display purposes (e.g., "ack")

    console.log('Device Online Status:', isOnline);
    console.log('Running Status:', isRunning);
    console.log('Current Value:', sensor.current_value?.value);

    // ================================
    // 🕒 Last Update Timestamp
    // ================================
    const lastUpdate = formatTimestamp(sensor.current_value?.timestamp || sensor.device_info?.last_update || null);

    // ================================
    // 📊 hasData based on engine hours availability
    // ================================
    const hasData = hasEngineHoursData;

    // ================================
    // 🎯 Get Status & Color (based on engine hours)
    // ================================
    const { status, color } = hasData ? getEngineHoursStatus(displayHours) : { status: 'No Data', color: 'text-gray-500' };

    // ================================
    // 🛠️ Maintenance Schedule Logic (Real Data)
    // ================================
    const getMaintenanceInfo = () => {
        if (!maintenanceStatus || maintenanceStatus.schedules.length === 0) {
            // Fallback to default 250h interval if no maintenance schedule
            const nextService = Math.ceil(displayHours / 250) * 250;
            const hoursUntilService = nextService - displayHours;
            return { 
                nextService, 
                hoursUntilService,
                hasMaintenance: false,
                overdueCount: 0,
                dueSoonCount: 0,
            };
        }

        // Find the most urgent schedule (overdue first, then due soon)
        const overdueSchedule = maintenanceStatus.schedules.find(s => s.is_overdue);
        const dueSoonSchedule = maintenanceStatus.schedules.find(s => s.is_due_soon && !s.is_overdue);
        const activeSchedule = overdueSchedule || dueSoonSchedule || maintenanceStatus.schedules[0];

        // Ensure numbers are properly converted
        const nextService = activeSchedule.next_service_run_hours 
            ? (typeof activeSchedule.next_service_run_hours === 'number' 
                ? activeSchedule.next_service_run_hours 
                : parseFloat(String(activeSchedule.next_service_run_hours)) || 0)
            : 0;
        const hoursUntilService = activeSchedule.hours_until !== undefined && activeSchedule.hours_until !== null
            ? (typeof activeSchedule.hours_until === 'number' 
                ? activeSchedule.hours_until 
                : parseFloat(String(activeSchedule.hours_until)) || 0)
            : 0;

        return {
            nextService,
            hoursUntilService,
            hasMaintenance: true,
            overdueCount: maintenanceStatus.overdue,
            dueSoonCount: maintenanceStatus.due_soon,
            schedule: activeSchedule,
        };
    };

    const maintenanceInfo = getMaintenanceInfo();
    const { nextService, hoursUntilService, hasMaintenance, overdueCount, dueSoonCount } = maintenanceInfo;

    // ================================
    // 💨 Running Animation (pulsing glow)
    // ================================
    const getRunningAnimation = () => {
        if (!isOnline || !hasData || !isRunning) return {};
        return {
            boxShadow: [
                '0 0 20px rgba(34, 197, 94, 0.3)',
                '0 0 40px rgba(34, 197, 94, 0.5)',
                '0 0 20px rgba(34, 197, 94, 0.3)',
            ],
            transition: {
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
            },
        };
    };

    // ================================
    // 🎨 Get Status Badge Info
    // ================================
    const getStatusBadge = () => {
        if (!isOnline) {
            return { text: 'Offline', className: 'bg-red-100 text-red-800' };
        }
        if (!hasData) {
            return { text: 'No Data', className: 'bg-yellow-100 text-yellow-800' };
        }
        if (isRunning) {
            return { text: 'Running', className: 'bg-green-100 text-green-800' };
        }
        return { text: 'Stopped', className: 'bg-gray-100 text-gray-800' };
    };

    const statusBadge = getStatusBadge();

    return (
        <>
            <div className="sensor-card cursor-pointer" onClick={() => setShowChart(true)}>
                {/* ==================================== */}
                {/* 🧭 Header with Status Badge */}
                {/* ==================================== */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="relative">
                            <Zap className="w-8 h-8 text-blue-600" />
                            {isOnline && (
                                <motion.div
                                    className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"
                                    animate={isRunning ? getRunningAnimation() : {}}
                                />
                            )}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">
                                {sensor.name}
                                {sensor.device_info?.name && (
                                    <span className="text-base font-normal text-gray-600 ml-2">
                                        ({sensor.device_info.name})
                                    </span>
                                )}
                            </h3>
                            <p className="text-sm text-gray-500">Generator Engine</p>
                        </div>
                    </div>

                    {/* ✅ Status Badge with Chart Button */}
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowChart(true);
                            }}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                            title="View Chart"
                        >
                            <BarChart3 className="w-4 h-4" />
                        </button>
                        {/* Maintenance Status Badge */}
                        {hasMaintenance && (overdueCount > 0 || dueSoonCount > 0) && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/maintenance');
                                }}
                                className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 transition-colors ${
                                    overdueCount > 0 
                                        ? 'bg-red-100 text-red-800 hover:bg-red-200' 
                                        : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                }`}
                            >
                                <Wrench className="w-4 h-4" />
                                <span>{overdueCount > 0 ? `${overdueCount} Overdue` : `${dueSoonCount} Due Soon`}</span>
                            </button>
                        )}
                        {/* Alert Count Badge */}
                        {sensor.alert_count !== undefined && sensor.alert_count > 0 && (
                            <div className="relative">
                                <div className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 flex items-center space-x-1">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span>{sensor.alert_count}</span>
                                </div>
                            </div>
                        )}
                        {/* Maintenance Settings Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/maintenance/schedules/new?generator_id=${sensor.id}`);
                            }}
                            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                            title="Manage Maintenance"
                        >
                            <Settings className="w-4 h-4" />
                        </button>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge.className}`}>
                            {statusBadge.text}
                        </div>
                        {isOnline && rawOnlineStatus === "ack" && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                Ack
                            </span>
                        )}
                    </div>
                </div>

                {/* ==================================== */}
                {/* 🖥️ Main Display — Engine Hours */}
                {/* ==================================== */}
                <motion.div
                    className="generator-display rounded-xl p-6 mb-6"
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    style={{
                        background: isRunning && isOnline ?
                            'linear-gradient(135deg, #22c55e, #16a34a)' :
                            'linear-gradient(135deg, #64748b, #475569)'
                    }}
                >
                    <div className="text-center">
                        <div className="text-white text-5xl font-mono mb-2">
                            {hasData ? displayHours.toLocaleString() : 'N/A'}
                        </div>
                        <div className="text-white/80 text-lg">Operating Hours</div>
                    </div>

                    {/* 🎯 Circular Progress to Next Service */}
                    {hasData && nextService > 0 && (
                        <div className="mt-6 relative">
                            <svg className="w-32 h-32 mx-auto transform -rotate-90" viewBox="0 0 120 120">
                                <circle
                                    cx="60"
                                    cy="60"
                                    r="54"
                                    fill="transparent"
                                    stroke="rgba(255,255,255,0.2)"
                                    strokeWidth="8"
                                />
                                <motion.circle
                                    cx="60"
                                    cy="60"
                                    r="54"
                                    fill="transparent"
                                    stroke="white"
                                    strokeWidth="8"
                                    strokeLinecap="round"
                                    strokeDasharray={`${(displayHours % Number(nextService)) / Number(nextService) * 339.292} 339.292`}
                                    initial={{ strokeDasharray: '0 339.292' }}
                                    animate={{ strokeDasharray: `${(displayHours % Number(nextService)) / Number(nextService) * 339.292} 339.292` }}
                                    transition={{ duration: 2, ease: 'easeOut' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center text-white">
                                    <div className="text-2xl font-bold">
                                        {Math.max(0, typeof hoursUntilService === 'number' 
                                            ? hoursUntilService 
                                            : parseFloat(String(hoursUntilService)) || 0).toFixed(0)}
                                    </div>
                                    <div className="text-sm opacity-80">hrs to service</div>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* ==================================== */}
                {/* 📊 Generator Status */}
                {/* ==================================== */}
                <div className="mb-6">
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 shadow-sm">
                        <div className="flex items-center space-x-2 mb-3">
                            <Zap className="w-5 h-5 text-gray-600" />
                            <span className="text-sm font-semibold text-gray-700">Generator Status</span>
                        </div>
                        <div className={`text-2xl font-bold ${
                            !isOnline ? 'text-red-600' :
                                isRunning ? 'text-green-600' : 'text-gray-600'
                        }`}>
                            {!isOnline ? 'Offline' : isRunning ? 'Running' : 'Stopped'}
                        </div>
                    </div>
                </div>

                {/* ==================================== */}
                {/* 📋 Nearest Upcoming Service */}
                {/* ==================================== */}
                {hasData && maintenanceStatus && maintenanceStatus.schedules.length > 0 && (() => {
                    // Find the nearest upcoming service (overdue first, then due soon, then upcoming)
                    const nearestService = maintenanceStatus.schedules
                        .sort((a, b) => {
                            // Sort by: overdue first, then by hours_until
                            if (a.is_overdue && !b.is_overdue) return -1;
                            if (!a.is_overdue && b.is_overdue) return 1;
                            const aHours = a.hours_until ?? Infinity;
                            const bHours = b.hours_until ?? Infinity;
                            return aHours - bHours;
                        })[0];
                    
                    if (!nearestService) return null;
                    
                    const hoursUntil = nearestService.hours_until ?? 0;
                    const isOverdue = nearestService.is_overdue;
                    const isDueSoon = nearestService.is_due_soon;
                    
                    return (
                        <div 
                            className={`rounded-lg border p-5 mb-6 cursor-pointer hover:shadow-lg transition-all shadow-sm ${
                                isOverdue
                                    ? 'bg-red-50 border-red-300'
                                    : isDueSoon
                                    ? 'bg-orange-50 border-orange-300'
                                    : 'bg-green-50 border-green-300'
                            }`}
                            onClick={() => navigate('/maintenance')}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2 mb-3">
                                        <Wrench className={`w-5 h-5 flex-shrink-0 ${
                                            isOverdue
                                                ? 'text-red-600'
                                                : isDueSoon
                                                ? 'text-orange-600'
                                                : 'text-green-600'
                                        }`} />
                                        <h3 className="text-lg font-semibold text-gray-900">Next Service</h3>
                                        {maintenanceStatus.schedules.length > 1 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate('/maintenance');
                                                }}
                                                className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-auto"
                                            >
                                                View All ({maintenanceStatus.schedules.length})
                                            </button>
                                        )}
                                    </div>
                                    <div className="font-semibold text-gray-900 mb-2 text-lg">
                                        {nearestService.template_name}
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <div>
                                            {nearestService.next_service_run_hours 
                                                ? `At ${(typeof nearestService.next_service_run_hours === 'number' ? nearestService.next_service_run_hours : parseFloat(String(nearestService.next_service_run_hours)) || 0).toFixed(0)}h`
                                                : nearestService.next_service_date
                                                ? `On ${new Date(nearestService.next_service_date).toLocaleDateString()}`
                                                : 'Not scheduled'}
                                        </div>
                                        {nearestService.days_until !== undefined && nearestService.days_until !== null && (
                                            <div className="text-xs text-gray-500">
                                                {nearestService.days_until < 0
                                                    ? `${Math.abs(nearestService.days_until)} days ago`
                                                    : nearestService.days_until === 0
                                                    ? 'Due today'
                                                    : `${nearestService.days_until} days remaining`}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className={`text-4xl font-bold ${
                                        isOverdue
                                            ? 'text-red-600'
                                            : isDueSoon
                                            ? 'text-orange-600'
                                            : 'text-green-600'
                                    }`}>
                                        {isOverdue
                                            ? 'Overdue'
                                            : hoursUntil >= 0
                                            ? `${(typeof hoursUntil === 'number' ? hoursUntil : parseFloat(String(hoursUntil)) || 0).toFixed(0)}h`
                                            : 'Due'}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 font-medium">
                                        remaining
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}
                
                {hasData && (!maintenanceStatus || maintenanceStatus.schedules.length === 0) && (
                    <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4 mb-6">
                        <div className="flex items-center space-x-2 mb-2">
                            <Wrench className="w-5 h-5 text-yellow-600" />
                            <span className="text-sm font-semibold text-yellow-900">No Maintenance Schedule</span>
                        </div>
                        <p className="text-sm text-yellow-800 mb-3">
                            Set up a maintenance schedule to track service intervals.
                        </p>
                        <button
                            onClick={() => navigate(`/maintenance/schedules/new?generator_id=${sensor.id}`)}
                            className="text-sm bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                        >
                            Create Schedule
                        </button>
                    </div>
                )}

                {/* ==================================== */}
                {/* ℹ️ Additional Info */}
                {/* ==================================== */}
                <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex justify-between">
                        <span>Device:</span>
                        <span className="font-medium">{sensor.device_info?.name || sensor.current_data?.name || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Last Update:</span>
                        <span className="font-medium">{lastUpdate}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Group:</span>
                        <span className="font-medium">{sensor.group.name}</span>
                    </div>
                    {sensor.location && (
                        <div className="flex justify-between">
                            <span>Location:</span>
                            <span className="font-medium flex items-center">
                                <MapPin className="w-3 h-3 mr-1" />
                                {sensor.location}
                            </span>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <span>Connection:</span>
                        <span className={`font-medium flex items-center ${
                            isOnline ? 'text-green-600' : 'text-red-600'
                        }`}>
                            <div className={`w-2 h-2 rounded-full mr-2 ${
                                isOnline ? 'bg-green-500' : 'bg-red-500'
                            }`} />
                            {isOnline ? 'Online' : 'Offline'}
                            {isOnline && rawOnlineStatus === "ack" && (
                                <span className="ml-1 text-xs text-blue-600">(Ack)</span>
                            )}
                        </span>
                    </div>
                </div>

                {/* ==================================== */}
                {/* ⚠️ Fuel Decrease Alerts */}
                {/* ==================================== */}
                {sensor.alert_count !== undefined && sensor.alert_count > 0 && (
                    <motion.div
                        className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold text-red-800">
                                        {sensor.alert_count} Fuel Decrease Alert{sensor.alert_count > 1 ? 's' : ''} (Last 7 Days)
                                    </h4>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowChart(true);
                                        }}
                                        className="text-xs text-red-600 hover:text-red-800 underline"
                                    >
                                        View Details
                                    </button>
                                </div>
                                {sensor.recent_alerts && sensor.recent_alerts.length > 0 && (
                                    <div className="space-y-2 mt-2">
                                        {sensor.recent_alerts.map((alert, index) => (
                                            <div key={alert.id || index} className="text-xs text-red-700 bg-red-100 rounded px-2 py-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium">{alert.alert_date}</span>
                                                    <span className="text-red-600">
                                                        {Number(alert.decrease_percent || 0).toFixed(1)}% decrease ({Number(alert.decrease_amount || 0).toFixed(1)}L)
                                                    </span>
                                                </div>
                                                <div className="text-red-600 mt-0.5">
                                                    {alert.start_timestamp}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* ==================================== */}
                {/* ⚠️ Maintenance Warning (if < 50h to service) */}
                {/* ==================================== */}
                {hasData && hoursUntilService < 50 && (
                    <motion.div
                        className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                    >
                        <div className="flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-orange-600" />
                            <span className="text-orange-800 text-sm font-medium">
                                Service due in {hoursUntilService} hours
                            </span>
                        </div>
                    </motion.div>
                )}

                {/* ==================================== */}
                {/* ❗ No Engine Hours Data Warning */}
                {/* ==================================== */}
                {!hasData && (
                    <motion.div
                        className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                    >
                        <div className="flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-gray-600" />
                            <span className="text-gray-800 text-sm font-medium">
                                {isOnline ? 'Engine hours data not available' : 'Generator offline - no data available'}
                            </span>
                        </div>
                    </motion.div>
                )}

                {/* ==================================== */}
                {/* 🔧 Debug Info (remove in production) */}
                {/* ==================================== */}
                {process.env.NODE_ENV === 'development' && (
                    <motion.div
                        className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                    >
                        <div className="text-blue-800 space-y-1">
                            <div><strong>Debug Info:</strong></div>
                            <div>Current Value: {JSON.stringify(sensor.current_value?.value)}</div>
                            <div>Device Status: {JSON.stringify(sensor.device_info?.status)}</div>
                            <div>Online Status: {JSON.stringify(sensor.current_data?.online)}</div>
                            <div>Engine Hours: {JSON.stringify(engineHoursValue)}</div>
                            <div>Is Running: {JSON.stringify(isRunning)}</div>
                            <div>Is Online: {JSON.stringify(isOnline)}</div>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Combined Chart Modal */}
            {showChart && (
                <GeneratorCombinedChart
                    generatorSensorId={sensor.id}
                    fuelSensorId={fuelSensorId}
                    generatorName={sensor.name}
                    deviceId={sensor.gpswox_device_id}
                    onClose={() => setShowChart(false)}
                />
            )}
        </>
    );
};

export default GeneratorDisplay;
